import { useState, useEffect } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { gamefuelAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  CreditCard, Zap, Clock, Shield, ChevronRight, Lock, Gift, X,
} from 'lucide-react';

export function CardPurchaseModal({ open, onClose, onSuccess }) {
  const { t } = useLanguage();
  const [amount, setAmount] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [gamefuelStatus, setGamefuelStatus] = useState(null);

  useEffect(() => {
    if (open) {
      gamefuelAPI.getStatus().then(r => setGamefuelStatus(r.data)).catch(() => {});
      setResult(null);
      setAmount('');
      setCardNumber('');
      setExpiry('');
      setCvv('');
    }
  }, [open]);

  if (!open) return null;

  const processingFee = amount ? (parseFloat(amount) * 0.035).toFixed(2) : '0.00';
  const netAmount = amount ? (parseFloat(amount) - parseFloat(processingFee)).toFixed(2) : '0.00';
  const gamefuelCredit = gamefuelStatus?.campaign_enabled
    ? (parseFloat(processingFee) * (gamefuelStatus?.credit_percentage || 100) / 100).toFixed(2)
    : '0.00';

  const handlePurchase = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    if (!cardNumber || cardNumber.length < 16) {
      toast.error('Enter a valid card number');
      return;
    }
    setLoading(true);
    try {
      const last4 = cardNumber.replace(/\s/g, '').slice(-4);
      const res = await gamefuelAPI.cardPurchase({
        amount: parseFloat(amount),
        currency: 'USDT',
        card_last4: last4,
      });
      setResult(res.data);
      toast.success(t('gamefuel.purchaseSuccess').replace('${amount}', res.data.net_credited));
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (val) => {
    const nums = val.replace(/\D/g, '').slice(0, 16);
    return nums.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (val) => {
    const nums = val.replace(/\D/g, '').slice(0, 4);
    if (nums.length > 2) return nums.slice(0, 2) + '/' + nums.slice(2);
    return nums;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[hsl(var(--border))]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[rgba(247,147,26,0.1)] flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-[#F7931A]" />
            </div>
            <div>
              <h3 className="font-semibold font-['Space_Grotesk']" data-testid="card-purchase-title">
                {t('gamefuel.buyWithCard')}
              </h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">USDT (TRC-20)</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[hsl(var(--secondary))] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {result ? (
          /* Success State */
          <div className="p-5 space-y-4" data-testid="card-purchase-success">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-[rgba(45,211,111,0.1)] flex items-center justify-center mx-auto">
                <Shield className="w-8 h-8 text-[#2DD36F]" />
              </div>
              <h4 className="text-lg font-semibold">{t('common.success')}</h4>
            </div>

            <div className="space-y-2 bg-[hsl(var(--secondary))]/50 rounded-xl p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-[hsl(var(--muted-foreground))]">{t('gamefuel.amount')}</span>
                <span className="font-mono">${result.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[hsl(var(--muted-foreground))]">{t('gamefuel.processingFee')}</span>
                <span className="font-mono text-[#D94A4A]">-${result.processing_fee}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>{t('gamefuel.netCredited')}</span>
                <span className="font-mono text-[#2DD36F]">${result.net_credited} USDT</span>
              </div>
              {result.gamefuel_credit > 0 && (
                <div className="flex justify-between pt-2 border-t border-[hsl(var(--border))]">
                  <span className="text-[#F7931A] flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5" /> {t('gamefuel.gamefuelCredit')}
                  </span>
                  <span className="font-mono text-[#F7931A] font-semibold">+${result.gamefuel_credit}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] bg-[rgba(247,200,75,0.05)] rounded-lg p-3 border border-[#F7C84B]/10">
              <Lock className="w-3.5 h-3.5 text-[#F7C84B] flex-shrink-0" />
              <span>{t('gamefuel.withdrawalLockDesc')}</span>
            </div>

            <Button onClick={onClose} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
              {t('common.close')}
            </Button>
          </div>
        ) : (
          /* Purchase Form */
          <div className="p-5 space-y-4">
            {/* GameFuel Banner */}
            {gamefuelStatus?.campaign_enabled && (
              <div className="bg-gradient-to-r from-[rgba(247,147,26,0.08)] to-[rgba(247,200,75,0.05)] border border-[#F7931A]/20 rounded-xl p-3 flex items-center gap-3" data-testid="gamefuel-banner">
                <Zap className="w-5 h-5 text-[#F7931A] flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-[#F7931A]">{t('gamefuel.title')}</p>
                  <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{t('gamefuel.feeCreditDesc')}</p>
                </div>
                <Badge className="bg-[rgba(247,147,26,0.15)] text-[#F7931A] border-[#F7931A]/20 text-[10px]">
                  {gamefuelStatus.credit_percentage}%
                </Badge>
              </div>
            )}

            {/* Amount */}
            <div>
              <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5 block">{t('gamefuel.amount')} (USD)</label>
              <Input
                data-testid="card-purchase-amount"
                type="number"
                min="10"
                step="10"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="100"
                className="bg-[hsl(var(--background))] border-[hsl(var(--border))] h-11 text-lg font-mono"
              />
              <div className="flex gap-2 mt-2">
                {[50, 100, 250, 500].map(v => (
                  <button
                    key={v}
                    onClick={() => setAmount(String(v))}
                    className="flex-1 text-xs py-1.5 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50 hover:bg-[hsl(var(--primary))]/5 transition-colors"
                  >
                    ${v}
                  </button>
                ))}
              </div>
            </div>

            {/* Card Details */}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5 block">{t('gamefuel.cardNumber')}</label>
                <Input
                  data-testid="card-number-input"
                  value={cardNumber}
                  onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                  placeholder="4242 4242 4242 4242"
                  className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono"
                  maxLength={19}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5 block">{t('gamefuel.cardExpiry')}</label>
                  <Input
                    data-testid="card-expiry-input"
                    value={expiry}
                    onChange={e => setExpiry(formatExpiry(e.target.value))}
                    placeholder="MM/YY"
                    className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono"
                    maxLength={5}
                  />
                </div>
                <div>
                  <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5 block">{t('gamefuel.cardCvv')}</label>
                  <Input
                    data-testid="card-cvv-input"
                    type="password"
                    value={cvv}
                    onChange={e => setCvv(e.target.value.replace(/\D/g, '').slice(0, 3))}
                    placeholder="***"
                    className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono"
                    maxLength={3}
                  />
                </div>
              </div>
            </div>

            {/* Fee Summary */}
            {amount && parseFloat(amount) > 0 && (
              <div className="bg-[hsl(var(--secondary))]/50 rounded-xl p-3 space-y-1.5 text-sm">
                <div className="flex justify-between text-[hsl(var(--muted-foreground))]">
                  <span>{t('gamefuel.processingFee')} (3.5%)</span>
                  <span className="font-mono">${processingFee}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('gamefuel.netCredited')}</span>
                  <span className="font-mono font-semibold">${netAmount} USDT</span>
                </div>
                {gamefuelStatus?.campaign_enabled && parseFloat(gamefuelCredit) > 0 && (
                  <div className="flex justify-between text-[#F7931A] pt-1.5 border-t border-[hsl(var(--border))]">
                    <span className="flex items-center gap-1">
                      <Zap className="w-3 h-3" /> {t('gamefuel.gamefuelCredit')}
                    </span>
                    <span className="font-mono font-semibold">+${gamefuelCredit}</span>
                  </div>
                )}
              </div>
            )}

            {/* Info */}
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] space-y-1">
              <p className="flex items-center gap-1"><Lock className="w-3 h-3" /> {t('gamefuel.withdrawalLockDesc')}</p>
              <p className="flex items-center gap-1"><Shield className="w-3 h-3" /> {t('gamefuel.kycRequired')}</p>
            </div>

            <Button
              data-testid="card-purchase-submit"
              onClick={handlePurchase}
              disabled={loading || !amount || parseFloat(amount) <= 0 || !cardNumber || cardNumber.length < 19}
              className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-11 gap-2"
            >
              {loading ? (
                t('gamefuel.purchasing')
              ) : (
                <>{t('gamefuel.purchaseBtn')} <ChevronRight className="w-4 h-4" /></>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
